import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { contactRequestSchema, type ContactRequest } from "@/contracts";

import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { TextAreaField, TextField } from "@/components/ui/field";
import { ApiError } from "@/lib/api-error";
import { apiClient } from "@/lib/api-client";

export function ContactForm() {
  const { t } = useTranslation("marketing");
  const [formError, setFormError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactRequest>({
    resolver: zodResolver(contactRequestSchema),
    defaultValues: { name: "", email: "", company: "", message: "" },
  });

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);

    try {
      const payload: ContactRequest = {
        name: values.name,
        email: values.email,
        message: values.message,
        ...(values.company?.trim() ? { company: values.company.trim() } : {}),
      };

      await apiClient.submitContact(payload);
      setSent(true);
      reset();
    } catch (error) {
      setFormError(messageFor(error, t));
    }
  });

  return (
    <form
      onSubmit={(event) => void onSubmit(event)}
      noValidate
      className="flex flex-col gap-4"
    >
      {sent ? (
        <Alert tone="info" announce>
          {t("contactSuccess")}
        </Alert>
      ) : null}

      {formError ? <Alert>{formError}</Alert> : null}

      <TextField
        label={t("contactName")}
        autoComplete="name"
        required
        {...(errors.name?.message === undefined ? {} : { error: errors.name.message })}
        {...register("name")}
      />

      <TextField
        label={t("contactEmail")}
        type="email"
        autoComplete="email"
        required
        {...(errors.email?.message === undefined ? {} : { error: errors.email.message })}
        {...register("email")}
      />

      <TextField
        label={t("contactCompany")}
        autoComplete="organization"
        {...(errors.company?.message === undefined ? {} : { error: errors.company.message })}
        {...register("company")}
      />

      <TextAreaField
        label={t("contactMessage")}
        hint={t("contactMessageHint")}
        required
        rows={5}
        {...(errors.message?.message === undefined ? {} : { error: errors.message.message })}
        {...register("message")}
      />

      <div>
        <Button type="submit" variant="primary" loading={isSubmitting}>
          {t("contactSubmit")}
        </Button>
      </div>
    </form>
  );
}

function messageFor(error: unknown, t: (key: string) => string): string {
  if (error instanceof ApiError) {
    if (error.code === "SERVICE_UNAVAILABLE") {
      return t("contactUnavailable");
    }

    return error.message || t("contactError");
  }

  return t("contactError");
}
